import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { CardRecord } from '../db/indexedDb';
import { segmentAudioEngine } from '../listening/audioEngine';
import { buildListeningCloze, scoreListeningCloze, scoreListeningSentence } from '../listening/scoring';
import { explainListeningWithLLM, hasListeningLLMSupport, scoreListeningWithLLM } from '../services/llm/listening';
import {
  ListeningClozeResult,
  ListeningContent,
  ListeningDiffChunk,
  ListeningResult,
  ListeningSegment,
  ListeningSentenceResult,
} from '../listening/types';
import { generateListeningClozeFromResult } from '../listening/clozeGenerator';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

// ============= 工具函数 =============

const buildTemplateParts = (template: string, blanks: number) => {
  const parts: Array<{ type: 'text' | 'blank'; value: string; index?: number }> = [];
  if (!template || blanks === 0) {
    parts.push({ type: 'text', value: template || '' });
    return parts;
  }

  const regex = /\{\{blank_(\d+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const [placeholder, slot] = match;
    const start = match.index;
    if (start > lastIndex) {
      parts.push({ type: 'text', value: template.slice(lastIndex, start) });
    }
    const idx = Number.parseInt(slot, 10);
    parts.push({ type: 'blank', value: placeholder, index: Number.isFinite(idx) ? idx : undefined });
    lastIndex = start + placeholder.length;
  }

  if (lastIndex < template.length) {
    parts.push({ type: 'text', value: template.slice(lastIndex) });
  }

  return parts;
};

const renderDiff = (diff: ListeningDiffChunk[]) =>
  diff.map((chunk, index) => {
    if (chunk.kind === 'hit')
      return (
        <span className="diff-chip hit" key={`diff-${index}`}>
          {chunk.expected}{' '}
        </span>
      );
    if (chunk.kind === 'missing')
      return (
        <span className="diff-chip missing" key={`diff-${index}`}>
          {chunk.expected}{' '}
        </span>
      );
    if (chunk.kind === 'extra')
      return (
        <span className="diff-chip extra" key={`diff-${index}`}>
          {chunk.actual}{' '}
        </span>
      );
    return (
      <span className="diff-chip substitution" key={`diff-${index}`}>
        {chunk.expected} → {chunk.actual}
      </span>
    );
  });

// ============= 组件 =============

export interface ListeningCardProps {
  card: CardRecord;
  onResult?: (result: ListeningResult) => void;
}

export function ListeningCard({ card, onResult }: ListeningCardProps) {
  const content = (card.content ?? {}) as ListeningContent;
  const segment = content.segment ?? {};
  const meta = card.listeningMeta ?? {};
  const transcript = segment.english ?? meta.transcript ?? '';

  const audioSources = useMemo(() => {
    const normalize = (src?: string) => {
      if (!src) return null;
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      return src.startsWith('/') ? src : `/${src}`;
    };
    const candidates = [
      normalize(meta.audioPath ?? content.audioPath),
      normalize(content.audioUrl),
      normalize(content.meta?.audioUrl),
      normalize(content.fallbackAudioUrl),
      normalize(meta.audioKey),
    ].filter(Boolean) as string[];
    return Array.from(new Set(candidates));
  }, [
    content.audioPath,
    content.audioUrl,
    content.fallbackAudioUrl,
    content.meta?.audioUrl,
    meta.audioKey,
    meta.audioPath,
  ]);

  const startMs = typeof meta.start === 'number' ? meta.start : segment.startMs ?? segment.start ?? 0;
  const endMs =
    typeof meta.end === 'number'
      ? meta.end
      : segment.endMs ?? segment.end ?? (segment.durationMs ? startMs + segment.durationMs : startMs + 3200);
  const chineseText = segment.chinese ?? (meta.extraMeta as Record<string, unknown> | undefined)?.['chinese'];

  const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const [audioError, setAudioError] = useState<string | null>(null);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const uiTimer = useRef<number>();

  useEffect(() => {
    setStatus('idle');
    setAudioError(null);
    segmentAudioEngine.stop();
    setResolvedSrc(null);
    const first = audioSources[0];
    // 预热时传入 startMs，提前 seek 到开始位置
    if (first) void segmentAudioEngine.warm(first, startMs);

    return () => {
      segmentAudioEngine.stop();
      if (uiTimer.current) {
        window.clearTimeout(uiTimer.current);
        uiTimer.current = undefined;
      }
    };
  }, [card.id, audioSources, startMs]);

  const play = async () => {
    if (audioSources.length === 0) {
      setAudioError('缺少音频资源，无法播放。');
      return;
    }
    try {
      setStatus('loading');
      setAudioError(null);
      let lastError: unknown;
      for (const src of audioSources) {
        try {
          await segmentAudioEngine.playSegment(src, startMs, endMs);
          setResolvedSrc(src);
          setStatus('playing');
          if (uiTimer.current) window.clearTimeout(uiTimer.current);
          uiTimer.current = window.setTimeout(() => setStatus('idle'), Math.max(endMs - startMs + 120, 0));
          return;
        } catch (err) {
          console.warn('[audio] source failed, trying next', src, err);
          lastError = err;
          segmentAudioEngine.stop();
        }
      }
      throw lastError ?? new Error('音频源不可用');
    } catch (err) {
      setStatus('error');
      setAudioError(err instanceof Error ? err.message : '播放失败');
    }
  };

  const audioMissing = meta.audioMissing ?? content.audioMissing;
  const label = content.meta?.title ?? '听力片段';

  // 判断是否为填空模式：必须同时有 cloze 模板和答案
  // 注意：不能依赖 card.mode，因为 SRS 调度器会根据反馈改变 mode
  // 源卡片可能有预设的 clozeTemplate 但没有 clozeAnswers，这种情况应该是整句听写模式
  const hasClozeAnswers = Boolean(
    (meta.clozeAnswers && meta.clozeAnswers.length > 0) ||
    ((content as Record<string, unknown>).clozeAnswers as string[] | undefined)?.length
  );
  // 只有当有 cloze 答案时才是填空模式
  const isSentenceMode = !hasClozeAnswers;

  // Sentence mode state
  const [sentenceAnswer, setSentenceAnswer] = useState('');
  const [sentenceResult, setSentenceResult] = useState<ListeningSentenceResult | null>(null);
  const [sentenceError, setSentenceError] = useState<string | null>(null);
  const [sentenceLlmState, setSentenceLlmState] = useState<'idle' | 'pending' | 'ready' | 'error' | 'skipped'>(
    'idle'
  );
  const [showSentenceAnswer, setShowSentenceAnswer] = useState(false);

  // Cloze mode state
  // 优先使用预生成的模板和答案，避免重复处理已有模板
  const { template: clozeTemplate, answers: clozeAnswers } = useMemo(() => {
    // 答案可能存储在 meta.clozeAnswers 或 content.clozeAnswers
    const existingAnswers = meta.clozeAnswers ?? (content as Record<string, unknown>).clozeAnswers as string[] | undefined;
    const existingTemplate = meta.clozeTemplate;
    // 如果已有预生成的模板和答案，直接使用
    if (existingTemplate && Array.isArray(existingAnswers) && existingAnswers.length > 0) {
      return { template: existingTemplate, answers: existingAnswers };
    }
    // 否则从原文生成
    const clozeSource = transcript ?? '';
    const clozeIndexes = meta.clozeIndexes ?? [];
    return buildListeningCloze(clozeSource, clozeIndexes);
  }, [card.id, meta.clozeTemplate, meta.clozeAnswers, meta.clozeIndexes, transcript, content]);
  const [clozeInputs, setClozeInputs] = useState<string[]>(() =>
    Array.from({ length: clozeAnswers.length || 0 }, () => '')
  );
  const [clozeResult, setClozeResult] = useState<ListeningClozeResult | null>(null);
  const [clozeError, setClozeError] = useState<string | null>(null);
  const [showClozeAnswer, setShowClozeAnswer] = useState(false);
  const clozeParts = useMemo(
    () => buildTemplateParts(clozeTemplate, clozeAnswers.length),
    [clozeTemplate, clozeAnswers.length]
  );

  // Reset state on card change
  useEffect(() => {
    setSentenceAnswer('');
    setSentenceResult(null);
    setSentenceError(null);
    setSentenceLlmState('idle');
    setShowSentenceAnswer(false);
    setClozeInputs(Array.from({ length: clozeAnswers.length || 0 }, () => ''));
    setClozeResult(null);
    setClozeError(null);
    setShowClozeAnswer(false);
  }, [card.id, clozeAnswers.length]);

  // 键盘快捷键处理
  const handlePlay = useCallback(() => {
    if (status !== 'loading') {
      play();
    }
  }, [status]);

  const handleToggleAnswer = useCallback(() => {
    if (isSentenceMode) {
      setShowSentenceAnswer((prev) => !prev);
    } else {
      setShowClozeAnswer((prev) => !prev);
    }
  }, [isSentenceMode]);

  // 全局快捷键（不在输入框内时生效）
  useKeyboardShortcuts({
    shortcuts: {
      ' ': handlePlay,  // Space = 播放
      'p': handlePlay,  // P = 播放
      'o': handleToggleAnswer,  // O = 显示/隐藏原文
    },
  });

  const handleSentenceSubmit = async () => {
    if (!transcript.trim()) {
      setSentenceError('缺少标准文本，无法比对');
      return;
    }
    if (!sentenceAnswer.trim()) {
      setSentenceError('请输入听写结果');
      return;
    }

    const base = scoreListeningSentence(transcript, sentenceAnswer);
    setSentenceResult(base);
    setSentenceError(null);
    setSentenceLlmState('idle');

    // 初始结果先通知父组件（延迟到下一个微任务，避免在渲染中更新父组件）
    queueMicrotask(() => onResult?.(base));

    // 并行处理LLM反馈和分档
    const llmPromises: Promise<void>[] = [];

    // LLM错误分析（可选）
    if (await hasListeningLLMSupport()) {
      setSentenceLlmState('pending');
      const feedbackPromise = explainListeningWithLLM({
        originalSentence: transcript,
        userAnswer: sentenceAnswer,
        diffSummary: base.diffSummary,
      })
        .then((feedback) => {
          const enriched: ListeningSentenceResult = {
            ...base,
            llm: { ...feedback, status: 'ready' },
          };
          setSentenceResult(enriched);
          setSentenceLlmState('ready');
          onResult?.(enriched);
        })
        .catch((err) => {
          console.warn('[listening] optional LLM feedback failed', err);
          const failed: ListeningSentenceResult = {
            ...base,
            llm: { errorTags: [], suggestions: [], provider: undefined, status: 'failed' },
          };
          setSentenceResult(failed);
          setSentenceLlmState('error');
          onResult?.(failed);
        });
      llmPromises.push(feedbackPromise);
    } else {
      const skipped: ListeningSentenceResult = {
        ...base,
        llm: { errorTags: [], suggestions: [], provider: undefined, status: 'skipped' },
      };
      setSentenceResult(skipped);
      setSentenceLlmState('skipped');
      queueMicrotask(() => onResult?.(skipped));
    }

    // 静默分档 - 异步执行，失败不阻塞
    const scoringPromise = scoreListeningWithLLM({
      originalSentence: transcript,
      userAnswer: sentenceAnswer,
      accuracy: base.accuracy,
      mode: 'sentence',
    })
      .then((llmScore) => {
        setSentenceResult((prev) => {
          if (!prev) return prev;
          const finalResult = { ...prev, llmScore };
          queueMicrotask(() => onResult?.(finalResult));
          return finalResult;
        });
      })
      .catch((err) => {
        console.warn('[listening] silent LLM scoring failed', err);
      });
    llmPromises.push(scoringPromise);

    // 等待所有LLM操作完成（但不阻塞UI）
    Promise.allSettled(llmPromises);

    // 生成听力挖空卡片（准确率 < 95% 时）
    if (base.accuracy < 95) {
      generateListeningClozeFromResult(card, base).catch((err) => {
        console.warn('[listening] cloze generation failed', err);
      });
    }
  };

  const evaluateCloze = () => {
    if (!clozeAnswers.length) {
      setClozeError('缺少挖空模板，稍后再试');
      return;
    }
    const result = scoreListeningCloze(clozeTemplate, clozeAnswers, clozeInputs, { includeHomophones: false });
    setClozeResult(result);
    setClozeError(null);
    queueMicrotask(() => onResult?.(result));

    // 静默分档 - 异步执行，失败不阻塞
    hasListeningLLMSupport().then((hasSupport) => {
      if (hasSupport) {
        scoreListeningWithLLM({
          originalSentence: transcript,
          userAnswer: clozeInputs.join(', '),
          accuracy: result.accuracy,
          mode: 'cloze',
        })
          .then((llmScore) => {
            setClozeResult((prev) => {
              if (!prev) return prev;
              return { ...prev, llmScore };
            });
            queueMicrotask(() => onResult?.({ ...result, llmScore }));
          })
          .catch((err) => {
            console.warn('[listening] silent LLM scoring failed for cloze', err);
          });
      }
    });
  };

  const fillClozeAnswer = (idx: number) => {
    setClozeInputs((prev) => {
      const next = [...prev];
      next[idx] = clozeAnswers[idx] ?? '';
      return next;
    });
  };

  return (
    <div className="practice-card listening-compact">
      {/* Compact header with audio controls inline */}
      <div className="practice-card-header listening-header">
        <h3 className="practice-question">{label}</h3>
        <button type="button" className="listening-play" onClick={play} disabled={status === 'loading'}>
          {status === 'playing' ? '⏸' : '▶'}
        </button>
      </div>

      {audioError && (
        <div className="error-text" style={{ padding: '8px 12px', fontSize: '12px' }}>
          {audioError}
        </div>
      )}

      {audioMissing ? (
        <p className="error-text">音频文件缺失，已标记占位。尝试更新资源或缓存后再试。</p>
      ) : null}


      {isSentenceMode ? (
        <div className="stack">
          <label className="section-label" htmlFor={`listening-answer-${card.id}`}>
            输入你听到的英文整句 (Space 播放)
          </label>
          <div className="listening-input-row">
            <textarea
              id={`listening-answer-${card.id}`}
              className="listening-textarea"
              placeholder="逐字听写，按 Enter 判分"
              rows={2}
              value={sentenceAnswer}
              onChange={(e) => setSentenceAnswer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSentenceSubmit();
                }
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <button className="listening-submit-btn" type="button" onClick={handleSentenceSubmit}>
              判分 ↵
            </button>
          </div>
          {sentenceError ? <p className="error-text">{sentenceError}</p> : null}
          {sentenceResult ? (
            <div className="recall-result">
              <div className="coverage-row">
                <div className="coverage-bar" aria-label="正确率">
                  <span className="coverage-fill" style={{ width: `${sentenceResult.accuracy}%` }} />
                </div>
                <span className="coverage-label">正确率 {sentenceResult.accuracy}%</span>
                {/* AI分档提示 */}
                {sentenceResult.llmScore?.status === 'ready' && (
                  <span className="pill micro success" style={{ marginLeft: '8px' }}>
                    小幅校准自 AI Band {sentenceResult.llmScore.band}
                  </span>
                )}
              </div>

              <div className="recall-block">
                <p className="section-label">错误高亮（漏写 / 替换 / 多写）</p>
                <div className="diff-panel">{renderDiff(sentenceResult.diff)}</div>
              </div>
              {sentenceResult.llm && sentenceResult.llm.status !== 'skipped' ? (
                <div className="recall-block">
                  <p className="section-label">AI 讲解</p>
                  {sentenceLlmState === 'pending' ? <p className="hint">生成中…</p> : null}
                  {sentenceLlmState === 'error' ? (
                    <p className="error-text">AI 讲解未完成，保留本地 diff。</p>
                  ) : null}
                  {sentenceLlmState === 'ready' ? (
                    <div className="ai-panel">
                      <p className="ai-label">错误类型</p>
                      <div className="pill-row">
                        {(sentenceResult.llm.errorTags ?? []).map((tag) => (
                          <span className="pill micro subtle" key={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="ai-label">训练建议</p>
                      <ul className="recall-list">
                        {(sentenceResult.llm.suggestions ?? []).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* 原文对照（可展开） */}
              <div className="recall-actions">
                <button
                  type="button"
                  className="session-action neutral"
                  onClick={() => setShowSentenceAnswer(!showSentenceAnswer)}
                >
                  {showSentenceAnswer ? '隐藏原文' : '显示原文'}
                </button>
              </div>
              {showSentenceAnswer && (
                <div className="recall-block">
                  <p className="section-label">原文对照</p>
                  <p className="session-body">{transcript || '文本待加载'}</p>
                  {chineseText ? <p className="session-meta muted">{chineseText as string}</p> : null}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="cloze-body">
          <div className="cloze-text">
            {clozeParts.map((part, idx) =>
              part.type === 'blank' && typeof part.index === 'number' ? (
                <span className="cloze-blank-wrap" key={`blank-${idx}`}>
                  <input
                    className="cloze-input"
                    aria-label={`空 ${part.index + 1}`}
                    value={clozeInputs[part.index] ?? ''}
                    onChange={(e) =>
                      setClozeInputs((prev) => {
                        const next = [...prev];
                        next[part.index as number] = e.target.value;
                        return next;
                      })
                    }
                    placeholder={`___`}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                </span>
              ) : (
                <span key={`text-${idx}`}>{part.value}</span>
              )
            )}
          </div>
          <div className="cloze-submit">
            <button className="cloze-btn" type="button" onClick={evaluateCloze}>
              判分
            </button>
          </div>
          {clozeError ? <p className="error-text">{clozeError}</p> : null}
          {clozeResult ? (
            <div className="cloze-result">
              <span className="cloze-score">{clozeResult.accuracy}%</span>
              <div className="cloze-answers">
                {clozeResult.blanks.map((blank) => (
                  <span 
                    key={blank.index} 
                    className={`cloze-answer ${blank.correct ? 'correct' : 'wrong'}`}
                  >
                    {blank.expected}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ListeningCard;
